/**
 * Blocos básicos da interface.
 *
 * Ficam num arquivo só de propósito: são componentes pequenos que quase sempre
 * são usados juntos, e espalhá-los em quinze arquivos de vinte linhas só
 * adicionaria caminhos de importação sem melhorar nada.
 */

import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, X } from 'lucide-react'
import { avatarColor, initials } from '@nexus/shared'
import { cn } from '@/lib/cn'

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

const AVATAR_SIZES = {
  xs: 'size-5 text-[9px]',
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-16 text-lg',
  xl: 'size-20 text-2xl',
} as const

const STATUS_SIZES = {
  xs: 'size-2 -right-0.5 -bottom-0.5 border-2',
  sm: 'size-3 -right-0.5 -bottom-0.5 border-2',
  md: 'size-3.5 -right-0.5 -bottom-0.5 border-[3px]',
  lg: 'size-5 right-0 bottom-0 border-4',
  xl: 'size-6 right-0.5 bottom-0.5 border-4',
} as const

const STATUS_COLORS: Record<string, string> = {
  online: 'bg-success',
  idle: 'bg-warning',
  dnd: 'bg-danger',
  offline: 'bg-content-muted',
}

export interface AvatarProps {
  userId: string
  name: string
  src?: string | null
  size?: keyof typeof AVATAR_SIZES
  status?: string | null
  /** Cor da borda do indicador de status, para casar com o fundo. */
  ringColor?: string
  speaking?: boolean
  className?: string
}

export function Avatar({
  userId,
  name,
  src,
  size = 'md',
  status,
  ringColor = 'border-surface-sunken',
  speaking,
  className,
}: AvatarProps) {
  const [failed, setFailed] = useState(false)
  const showImage = src && !failed

  return (
    <div className={cn('relative shrink-0', className)}>
      <div
        className={cn(
          'flex items-center justify-center overflow-hidden rounded-full font-semibold text-white select-none',
          AVATAR_SIZES[size],
          // O realce é todo em sombra, que não ocupa espaço: desenhado como
          // borda, ele empurraria os vizinhos em listas compactas.
          speaking && 'falando',
          'transition-shadow duration-150',
        )}
        style={showImage ? undefined : { backgroundColor: avatarColor(userId) }}
      >
        {showImage ? (
          <img
            src={src}
            alt=""
            className="size-full object-cover"
            loading="lazy"
            onError={() => setFailed(true)}
          />
        ) : (
          initials(name)
        )}
      </div>

      {status ? (
        <span
          className={cn(
            'absolute rounded-full',
            STATUS_SIZES[size],
            STATUS_COLORS[status] ?? STATUS_COLORS.offline,
            ringColor,
          )}
          aria-label={`Status: ${status}`}
        />
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Botão
// ---------------------------------------------------------------------------

const BUTTON_VARIANTS = {
  primary: 'bg-brand text-white hover:bg-brand-hover active:bg-brand-active',
  secondary:
    'bg-surface-overlay text-content-primary hover:bg-surface-hover active:bg-surface-active',
  ghost: 'text-content-secondary hover:bg-surface-hover hover:text-content-primary',
  danger: 'bg-danger text-white hover:bg-danger-hover',
  'danger-ghost': 'text-danger hover:bg-danger-subtle',
  outline:
    'border border-line-strong text-content-primary hover:bg-surface-hover hover:border-content-muted',
} as const

const BUTTON_SIZES = {
  xs: 'h-7 px-2.5 text-xs rounded-md gap-1.5',
  sm: 'h-8 px-3 text-[13px] rounded-md gap-1.5',
  md: 'h-9 px-4 text-sm rounded-lg gap-2',
  lg: 'h-11 px-5 text-[15px] rounded-lg gap-2',
  icon: 'size-9 rounded-lg',
  'icon-sm': 'size-8 rounded-md',
} as const

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof BUTTON_VARIANTS
  size?: keyof typeof BUTTON_SIZES
  loading?: boolean
  full?: boolean
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  full,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center font-medium whitespace-nowrap',
        'transition-colors duration-100',
        'disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        full && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Spinner className="size-4" /> : children}
    </button>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cn('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Campos de formulário
// ---------------------------------------------------------------------------

export interface FieldProps {
  label?: string
  hint?: string
  error?: string
  required?: boolean
  children: ReactNode
  className?: string
}

export function Field({ label, hint, error, required, children, className }: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label ? (
        <label className="text-xs font-semibold tracking-wide text-content-secondary uppercase">
          {label}
          {required ? <span className="ml-1 text-danger">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="text-xs text-content-muted">{hint}</p>
      ) : null}
    </div>
  )
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        'h-10 w-full rounded-lg bg-surface-deepest px-3',
        'border border-transparent text-content-primary',
        'transition-colors focus:border-brand focus:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
      {...props}
    />
  )
}

/**
 * Campo de escolha.
 *
 * Usa o `select` do próprio navegador em vez de uma lista desenhada à mão. A
 * lista aberta sai com a cara do sistema, o que destoa um pouco, mas em troca
 * vem de graça o teclado, a leitura de tela e o comportamento certo no celular.
 */
export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select
        className={cn(
          'h-10 w-full appearance-none rounded-lg bg-surface-deepest pr-9 pl-3',
          'border border-transparent text-content-primary',
          'transition-colors focus:border-brand focus:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-content-muted" />
    </div>
  )
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(
        'w-full resize-none rounded-lg bg-surface-deepest px-3 py-2.5',
        'border border-transparent text-content-primary',
        'transition-colors focus:border-brand focus:outline-none',
        className,
      )}
      {...props}
    />
  )
}

export interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  description?: string
  disabled?: boolean
}

export function Switch({ checked, onChange, label, description, disabled }: SwitchProps) {
  const id = useId()

  return (
    <div className="flex items-start justify-between gap-4 py-2">
      {label ? (
        <div className="min-w-0 flex-1">
          <label htmlFor={id} className="cursor-pointer text-sm font-medium text-content-primary">
            {label}
          </label>
          {description ? (
            <p className="mt-0.5 text-xs leading-relaxed text-content-muted">{description}</p>
          ) : null}
        </div>
      ) : null}

      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors duration-150',
          checked ? 'bg-brand' : 'bg-surface-active',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {/*
          O `left-1` é obrigatório, não decorativo. Sem ele o thumb fica com
          `left: auto` e cai na posição estática, que num `<button>` é o centro
          por causa do `text-align: center` do agente de usuário. O translate
          parte desse centro e joga a bolinha para fora do trilho.

          Com origem fixa em 4px, o deslocamento é: 44 (trilho) - 16 (thumb)
          - 4 (folga) - 4 (origem) = 20px, ou seja, translate-x-5.
        */}
        <span
          className={cn(
            'absolute top-1 left-1 size-4 rounded-full bg-white transition-transform duration-150',
            checked ? 'translate-x-5' : 'translate-x-0',
          )}
        />
      </button>
    </div>
  )
}

export interface SliderProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  label?: string
  formatValue?: (value: number) => string
  disabled?: boolean
}

export function Slider({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  label,
  formatValue,
  disabled,
}: SliderProps) {
  const percent = ((value - min) / (max - min)) * 100

  return (
    <div className="py-2">
      {label ? (
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-content-primary">{label}</span>
          <span className="font-mono text-xs text-content-secondary">
            {formatValue ? formatValue(value) : value}
          </span>
        </div>
      ) : null}

      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className={cn(
          'h-1.5 w-full cursor-pointer appearance-none rounded-full',
          '[&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none',
          '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white',
          '[&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform',
          '[&::-webkit-slider-thumb]:hover:scale-110',
          '[&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:rounded-full',
          '[&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        style={{
          background: `linear-gradient(to right, var(--color-brand) ${percent}%, var(--color-surface-active) ${percent}%)`,
        }}
      />
    </div>
  )
}

/**
 * Onde pendurar o que flutua por cima da interface.
 *
 * Normalmente é o `body`. Só que, com um elemento em tela cheia, o navegador
 * pinta apenas esse elemento e o que está dentro dele: um menu pendurado no
 * `body` continua existindo, responde a clique, e simplesmente não aparece.
 *
 * Foi assim que o volume da transmissão ficou inalcançável em tela cheia, que é
 * justamente quando alguém está assistindo e quer mexer no som.
 */
export function useCamadaDeSobreposicao(): Element {
  const [camada, setCamada] = useState<Element>(() =>
    typeof document === 'undefined' ? (null as unknown as Element) : document.body,
  )

  useEffect(() => {
    const atualizar = () => setCamada(document.fullscreenElement ?? document.body)
    atualizar()

    document.addEventListener('fullscreenchange', atualizar)
    return () => document.removeEventListener('fullscreenchange', atualizar)
  }, [])

  return camada
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Impede fechar por clique fora ou Esc, para operações destrutivas. */
  persistent?: boolean
}

const MODAL_SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
} as const

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  persistent,
}: ModalProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const camada = useCamadaDeSobreposicao()

  /**
   * O `onClose` recebido costuma ser uma função inline do componente pai, e
   * portanto uma referência nova a cada render dele. Guardá-la num ref permite
   * que o efeito abaixo dependa apenas de `open`.
   *
   * Sem isso, digitar num campo dentro do modal re-renderizava o pai, criava
   * um novo `onClose`, o efeito rodava de novo e o `focus()` no container
   * tirava o foco do campo a cada tecla.
   */
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !persistent) onCloseRef.current()
    }
    document.addEventListener('keydown', onKeyDown)

    // Trava a rolagem do fundo enquanto o modal está aberto.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // O foco vai para dentro do modal, senão o teclado continua navegando a
    // página atrás dele. Campos com `autoFocus` já se posicionaram neste
    // ponto, e roubar deles seria desfazer a intenção de quem montou a tela.
    const container = contentRef.current
    if (container && !container.contains(document.activeElement)) {
      container.focus()
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [open, persistent])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px] animate-fade-in"
        onClick={persistent ? undefined : onClose}
      />

      <div
        ref={contentRef}
        tabIndex={-1}
        className={cn(
          'relative w-full overflow-hidden rounded-xl bg-surface-raised shadow-2xl',
          'animate-slide-up outline-none',
          MODAL_SIZES[size],
        )}
      >
        {title ? (
          <header className="flex items-start justify-between gap-4 px-6 pt-5 pb-4">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-content-primary">{title}</h2>
              {description ? (
                <p className="mt-1 text-sm leading-relaxed text-content-secondary">{description}</p>
              ) : null}
            </div>
            {!persistent ? (
              <button
                type="button"
                onClick={onClose}
                aria-label="Fechar"
                className="-mt-1 -mr-2 rounded-md p-1.5 text-content-muted transition-colors hover:bg-surface-hover hover:text-content-primary"
              >
                <X className="size-5" />
              </button>
            ) : null}
          </header>
        ) : null}

        <div className="max-h-[70vh] overflow-y-auto px-6 pb-6">{children}</div>

        {footer ? (
          <footer className="flex justify-end gap-2 bg-surface-base px-6 py-4">{footer}</footer>
        ) : null}
      </div>
    </div>,
    camada,
  )
}

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

export interface TooltipProps {
  content: ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  children: ReactNode
  delay?: number
}

/** Distância mínima entre o balão e a borda da janela. */
const TOOLTIP_MARGEM = 8

export function Tooltip({ content, side = 'top', children, delay = 400 }: TooltipProps) {
  const [visible, setVisible] = useState(false)
  const camada = useCamadaDeSobreposicao()
  const [coords, setCoords] = useState({ x: 0, y: 0 })
  const [ajuste, setAjuste] = useState({ dx: 0, dy: 0 })
  const triggerRef = useRef<HTMLSpanElement>(null)
  const balaoRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<number | null>(null)

  const show = () => {
    timerRef.current = window.setTimeout(() => {
      /*
        A medida vem do filho, não do invólucro.

        O invólucro usa `display: contents` para não interferir no layout de
        quem envolve, e um elemento assim não gera caixa própria: o retângulo
        dele é sempre zero em todos os lados. O balão ia parar no canto superior
        esquerdo da janela, longe do botão que o abriu, em toda dica da
        aplicação.
      */
      const alvo = triggerRef.current?.firstElementChild ?? triggerRef.current
      const rect = alvo?.getBoundingClientRect()
      if (!rect || (rect.width === 0 && rect.height === 0)) return

      const positions = {
        top: { x: rect.left + rect.width / 2, y: rect.top - 8 },
        bottom: { x: rect.left + rect.width / 2, y: rect.bottom + 8 },
        left: { x: rect.left - 8, y: rect.top + rect.height / 2 },
        right: { x: rect.right + 8, y: rect.top + rect.height / 2 },
      }
      setCoords(positions[side])
      setAjuste({ dx: 0, dy: 0 })
      setVisible(true)
    }, delay)
  }

  /*
    Empurra o balão de volta para dentro da janela.

    A posição vinha só do elemento que dispara o tooltip, sem olhar o tamanho do
    balão nem onde termina a tela. Perto das bordas, principalmente na barra
    lateral e no topo, o texto saía para fora e ficava cortado. A medida só é
    possível depois de renderizar, então corrigimos num segundo passo, antes da
    pintura, para não aparecer o pulo.
  */
  useLayoutEffect(() => {
    if (!visible) return
    const balao = balaoRef.current
    if (!balao) return

    const caixa = balao.getBoundingClientRect()
    let dx = 0
    let dy = 0

    if (caixa.left < TOOLTIP_MARGEM) dx = TOOLTIP_MARGEM - caixa.left
    else if (caixa.right > window.innerWidth - TOOLTIP_MARGEM) {
      dx = window.innerWidth - TOOLTIP_MARGEM - caixa.right
    }

    if (caixa.top < TOOLTIP_MARGEM) dy = TOOLTIP_MARGEM - caixa.top
    else if (caixa.bottom > window.innerHeight - TOOLTIP_MARGEM) {
      dy = window.innerHeight - TOOLTIP_MARGEM - caixa.bottom
    }

    if (dx !== 0 || dy !== 0) setAjuste({ dx, dy })
  }, [visible, coords])

  const hide = () => {
    if (timerRef.current !== null) clearTimeout(timerRef.current)
    setVisible(false)
  }

  useEffect(() => () => {
    if (timerRef.current !== null) clearTimeout(timerRef.current)
  }, [])

  const transforms = {
    top: 'translate(-50%, -100%)',
    bottom: 'translate(-50%, 0)',
    left: 'translate(-100%, -50%)',
    right: 'translate(0, -50%)',
  }

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="contents"
      >
        {children}
      </span>

      {visible
        ? createPortal(
            <div
              ref={balaoRef}
              role="tooltip"
              className="pointer-events-none fixed z-[100] max-w-xs rounded-md bg-surface-deepest px-2.5 py-1.5 text-xs font-medium text-content-primary shadow-lg animate-fade-in"
              style={{
                left: coords.x + ajuste.dx,
                top: coords.y + ajuste.dy,
                transform: transforms[side],
              }}
            >
              {content}
            </div>,
            camada,
          )
        : null}
    </>
  )
}

// ---------------------------------------------------------------------------
// Menu de contexto
// ---------------------------------------------------------------------------

interface MenuContextValue {
  close: () => void
}
const MenuContext = createContext<MenuContextValue>({ close: () => {} })

export interface ContextMenuProps {
  x: number
  y: number
  onClose: () => void
  children: ReactNode
}

export function ContextMenu({ x, y, onClose, children }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null)
  const camada = useCamadaDeSobreposicao()
  const [position, setPosition] = useState({ x, y })

  // Mesmo motivo do Modal: `onClose` costuma ser inline no pai, e sem o ref os
  // listeners seriam removidos e registrados de novo a cada render dele.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) onCloseRef.current()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  // Reposiciona quando o menu não cabe na direção original.
  useEffect(() => {
    const rect = ref.current?.getBoundingClientRect()
    if (!rect) return

    let nextX = x
    let nextY = y
    if (x + rect.width > window.innerWidth - 8) nextX = window.innerWidth - rect.width - 8
    if (y + rect.height > window.innerHeight - 8) nextY = y - rect.height

    if (nextX !== position.x || nextY !== position.y) setPosition({ x: nextX, y: nextY })
  }, [x, y, position.x, position.y])

  return createPortal(
    <MenuContext.Provider value={{ close: onClose }}>
      <div
        ref={ref}
        role="menu"
        className="fixed z-[90] max-h-[80vh] min-w-52 overflow-y-auto overscroll-contain rounded-lg bg-surface-overlay p-1.5 shadow-2xl animate-fade-in"
        style={{ left: position.x, top: position.y }}
      >
        {children}
      </div>
    </MenuContext.Provider>,
    camada,
  )
}

export interface MenuItemProps {
  icon?: ReactNode
  children: ReactNode
  onClick?: () => void
  danger?: boolean
  disabled?: boolean
  shortcut?: string
}

export function MenuItem({ icon, children, onClick, danger, disabled, shortcut }: MenuItemProps) {
  const { close } = useContext(MenuContext)

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        onClick?.()
        close()
      }}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-40',
        danger
          ? 'text-danger hover:bg-danger hover:text-white'
          : 'text-content-secondary hover:bg-brand hover:text-white',
      )}
    >
      {icon ? <span className="shrink-0 [&>svg]:size-4">{icon}</span> : null}
      <span className="flex-1 text-left">{children}</span>
      {shortcut ? <span className="text-xs opacity-60">{shortcut}</span> : null}
    </button>
  )
}

export function MenuSeparator() {
  return <div className="my-1 h-px bg-line" />
}

// ---------------------------------------------------------------------------
// Diversos
// ---------------------------------------------------------------------------

export function Badge({
  children,
  variant = 'default',
  className,
}: {
  children: ReactNode
  variant?: 'default' | 'brand' | 'success' | 'warning' | 'danger'
  className?: string
}) {
  const variants = {
    default: 'bg-surface-overlay text-content-secondary',
    brand: 'bg-brand-subtle text-brand',
    success: 'bg-success-subtle text-success',
    warning: 'bg-warning-subtle text-warning',
    danger: 'bg-danger-subtle text-danger',
  }

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        variants[variant],
        className,
      )}
    >
      {children}
    </span>
  )
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      {icon ? <div className="mb-4 text-content-muted [&>svg]:size-12">{icon}</div> : null}
      <h3 className="text-base font-semibold text-content-primary">{title}</h3>
      {description ? (
        <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-content-secondary">{description}</p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  )
}

export function Divider({ label, className }: { label?: string; className?: string }) {
  if (!label) return <div className={cn('h-px bg-line', className)} />

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div className="h-px flex-1 bg-line" />
      <span className="text-xs font-semibold tracking-wide text-content-muted uppercase">
        {label}
      </span>
      <div className="h-px flex-1 bg-line" />
    </div>
  )
}
